import { useEffect, useRef } from "react"

// ── Gaze computation ──────────────────────────────────────────────────────────
function computeGaze(lm, prevFrame) {
  const eyeWidth = Math.abs(lm[133].x - lm[33].x)
  const irisRatioH = eyeWidth > 0 ? (lm[468].x - lm[33].x) / eyeWidth : 0.5

  const eyeHeight = Math.abs(lm[145].y - lm[159].y)
  const irisRatioV = eyeHeight > 0 ? (lm[468].y - lm[159].y) / eyeHeight : 0.5

  const headYaw = (lm[1].x - 0.5) * 90
  const headPitch = (lm[1].y - 0.5) * 90

  const azimuthDeg = (irisRatioH - 0.5) * 90 - headYaw
  const elevationDeg = (irisRatioV - 0.5) * 90 - headPitch

  // Velocity — how much gaze changed since last frame
  const velocityH = prevFrame ? Math.abs(azimuthDeg - prevFrame.azimuthDeg) : 0
  const velocityV = prevFrame
    ? Math.abs(elevationDeg - prevFrame.elevationDeg)
    : 0
  const totalVelocity = Math.sqrt(velocityH ** 2 + velocityV ** 2)

  // Classify into stability bucket
  const stability =
    totalVelocity < 3
      ? "stable" // barely moving — locked onto something
      : totalVelocity < 12
        ? "partial_stable" // some movement but not erratic
        : "unstable" // fast movement — natural thinking/scanning

  return {
    ts: Date.now(),
    azimuthDeg: parseFloat(azimuthDeg.toFixed(2)),
    elevationDeg: parseFloat(elevationDeg.toFixed(2)),
    headYaw: parseFloat(headYaw.toFixed(2)),
    headPitch: parseFloat(headPitch.toFixed(2)),
    irisRatioH: parseFloat(irisRatioH.toFixed(3)),
    irisRatioV: parseFloat(irisRatioV.toFixed(3)),
    stability, // "stable" | "partial_stable" | "unstable"
  }
}

// ── Main component ────────────────────────────────────────────────────────────
const FacialLandmarksMirror = ({
  stream,
  isCameraOn,
  gazeLogRef,
  onGaugeFrame,
}) => {
  const videoRef = useRef(null)
  const canvasRef = useRef(null)
  const animFrameRef = useRef(null)
  const lastDotsRef = useRef([])
  const frameCountRef = useRef(0)
  const prevGazeRef = useRef(null)

  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream
      videoRef.current.play().catch(() => {})
    }
  }, [stream])

  // ── Load MediaPipe + run inference ────────────────────────────────────────
  useEffect(() => {
    if (!isCameraOn) return
    let cancelled = false
    let camera = null
    let faceMesh = null

    const loadScript = (src) =>
      new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) return resolve()
        const s = document.createElement("script")
        s.src = src
        s.crossOrigin = "anonymous"
        s.onload = resolve
        s.onerror = reject
        document.head.appendChild(s)
      })

    const waitFor = (fn) =>
      new Promise((resolve) => {
        const check = () => (fn() ? resolve() : setTimeout(check, 100))
        check()
      })

    const init = async () => {
      await loadScript(
        "https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh@0.4/face_mesh.js",
      )
      await loadScript(
        "https://cdn.jsdelivr.net/npm/@mediapipe/camera_utils@0.3/camera_utils.js",
      )
      await waitFor(() => window.FaceMesh && window.Camera)
      if (cancelled) return

      faceMesh = new window.FaceMesh({
        locateFile: (file) =>
          `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh@0.4/${file}`,
      })

      faceMesh.setOptions({
        maxNumFaces: 1,
        refineLandmarks: true,
        minDetectionConfidence: 0.5,
        minTrackingConfidence: 0.5,
      })

      faceMesh.onResults((results) => {
        if (cancelled) return
        const video = videoRef.current
        const canvas = canvasRef.current
        if (!canvas || !video) return

        const scaleX = canvas.width / (video.videoWidth || 1)
        const scaleY = canvas.height / (video.videoHeight || 1)

        if (results.multiFaceLandmarks?.[0]) {
          const lm = results.multiFaceLandmarks[0]

          // ── Dots for canvas ──────────────────────────────────────
          lastDotsRef.current = lm.map((pt) => ({
            x: pt.x * (video.videoWidth || 1) * scaleX,
            y: pt.y * (video.videoHeight || 1) * scaleY,
          }))
          frameCountRef.current += 1

          // ── Gaze calculation ─────────────────────────────────────
          const gazeEvent = computeGaze(lm, prevGazeRef.current)
          prevGazeRef.current = gazeEvent

          // Push to parent's gazeLogRef if provided
          if (gazeLogRef?.current) {
            gazeLogRef.current.push(gazeEvent)
          }
          onGaugeFrame?.(gazeEvent)
        } else {
          lastDotsRef.current = []
          prevGazeRef.current = null
        }
      })

      camera = new window.Camera(videoRef.current, {
        onFrame: async () => {
          if (videoRef.current && faceMesh)
            await faceMesh.send({ image: videoRef.current })
        },
        width: 320,
        height: 240,
      })

      camera.start()
    }

    init().catch((err) => console.warn("[FacialLandmarksMirror]", err))

    return () => {
      cancelled = true
      camera?.stop()
      faceMesh?.close()
    }
  }, [isCameraOn])

  // ── Draw loop ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isCameraOn) return

    const drawFrame = () => {
      const canvas = canvasRef.current
      if (!canvas) {
        animFrameRef.current = requestAnimationFrame(drawFrame)
        return
      }

      canvas.width = canvas.offsetWidth
      canvas.height = canvas.offsetHeight

      const ctx = canvas.getContext("2d")
      const W = canvas.width
      const H = canvas.height
      const dots = lastDotsRef.current

      ctx.clearRect(0, 0, W, H)

      if (dots.length > 0) {
        // Mesh dots
        for (let i = 0; i < dots.length; i++) {
          const isIris = i >= 468
          ctx.beginPath()
          ctx.arc(dots[i].x, dots[i].y, isIris ? 2 : 0.9, 0, Math.PI * 2)
          ctx.fillStyle = isIris ? "rgba(0,0,0,0.9)" : "rgba(0,0,0,0.35)"
          ctx.fill()
        }

        // Iris crosshairs
        const irisPoints = [dots[468], dots[473]].filter(Boolean)
        for (const iris of irisPoints) {
          const x = iris.x,
            y = iris.y
          ctx.strokeStyle = "rgba(0,0,0,0.7)"
          ctx.lineWidth = 0.8
          ctx.beginPath()
          ctx.moveTo(x - 7, y)
          ctx.lineTo(x + 7, y)
          ctx.moveTo(x, y - 7)
          ctx.lineTo(x, y + 7)
          ctx.stroke()
          ctx.beginPath()
          ctx.arc(x, y, 3.5, 0, Math.PI * 2)
          ctx.strokeStyle = "rgba(0,0,0,0.5)"
          ctx.lineWidth = 0.8
          ctx.stroke()
        }

        // Face bounding box corner brackets
        const xs = dots.map((d) => d.x)
        const ys = dots.map((d) => d.y)
        const minX = Math.min(...xs) - 12
        const maxX = Math.max(...xs) + 12
        const minY = Math.min(...ys) - 12
        const maxY = Math.max(...ys) + 12
        const bLen = 14

        ctx.strokeStyle = "rgba(0,0,0,0.6)"
        ctx.lineWidth = 1.5

        const corners = [
          [minX, minY, bLen, 0, 0, bLen],
          [maxX, minY, -bLen, 0, 0, bLen],
          [minX, maxY, bLen, 0, 0, -bLen],
          [maxX, maxY, -bLen, 0, 0, -bLen],
        ]
        for (const [x, y, dx1, dy1, dx2, dy2] of corners) {
          ctx.beginPath()
          ctx.moveTo(x + dx1, y + dy1)
          ctx.lineTo(x, y)
          ctx.lineTo(x + dx2, y + dy2)
          ctx.stroke()
        }

        // Gaze readout overlay — bottom left
        const gaze = prevGazeRef.current
        if (gaze) {
          const lines = [
            `AZ  ${gaze.azimuthDeg > 0 ? "+" : ""}${(gaze.azimuthDeg ?? 0).toFixed(1)}°`,
            `EL  ${gaze.elevationDeg > 0 ? "+" : ""}${(gaze.elevationDeg ?? 0).toFixed(1)}°`,
            `YAW ${gaze.headYaw > 0 ? "+" : ""}${(gaze.headYaw ?? 0).toFixed(1)}°`,
            `STB ${gaze.stability ?? "—"}`,
          ]
          ctx.font = "500 8.5px 'IBM Plex Mono', monospace"
          ctx.textAlign = "left"
          lines.forEach((line, i) => {
            const alpha = i === 0 || i === 1 ? "0.55" : "0.3"
            ctx.fillStyle = `rgba(0,0,0,${alpha})`
            ctx.fillText(line, 10, H - 10 - (lines.length - 1 - i) * 13)
          })
        }

        // Frame counter — bottom right
        ctx.font = "500 9px 'IBM Plex Mono', monospace"
        ctx.fillStyle = "rgba(0,0,0,0.25)"
        ctx.textAlign = "right"
        ctx.fillText(
          `FRAME ${String(frameCountRef.current).padStart(5, "0")}`,
          W - 10,
          H - 10,
        )
      } else {
        // No face — scanning animation
        const cx = W / 2
        const cy = H / 2
        const t = Date.now() / 1000
        const r = 28 + Math.sin(t * 2) * 4

        ctx.strokeStyle = "rgba(0,0,0,0.1)"
        ctx.lineWidth = 1
        ctx.beginPath()
        ctx.arc(cx, cy, r, 0, Math.PI * 2)
        ctx.stroke()

        ctx.strokeStyle = "rgba(0,0,0,0.35)"
        ctx.lineWidth = 1.5
        ctx.beginPath()
        ctx.arc(cx, cy, r, t * 2, t * 2 + Math.PI * 0.6)
        ctx.stroke()

        ctx.font = "600 9px 'IBM Plex Mono', monospace"
        ctx.fillStyle = "rgba(0,0,0,0.25)"
        ctx.textAlign = "center"
        ctx.fillText("NO FACE DETECTED", cx, cy + r + 18)
      }

      animFrameRef.current = requestAnimationFrame(drawFrame)
    }

    animFrameRef.current = requestAnimationFrame(drawFrame)
    return () => cancelAnimationFrame(animFrameRef.current)
  }, [isCameraOn])

  return (
    <div
      className="relative w-full h-full flex flex-col"
      style={{ background: "#fff", borderRadius: "12px", overflow: "hidden" }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-3 shrink-0"
        style={{
          height: "32px",
          borderBottom: "1px solid rgba(0,0,0,0.08)",
          fontFamily: "'IBM Plex Mono', monospace",
        }}
      >
        <div className="flex items-center gap-2">
          <span
            className="w-1.5 h-1.5 rounded-full animate-pulse"
            style={{ background: "#000" }}
          />
          <span
            style={{
              fontSize: "9px",
              fontWeight: 600,
              letterSpacing: "0.15em",
              color: "#000",
            }}
          >
            FACIAL ANALYSIS
          </span>
        </div>
        <span
          style={{
            fontSize: "9px",
            color: "rgba(0,0,0,0.3)",
            letterSpacing: "0.1em",
          }}
        >
          468 PTS · IRIS · GAZE
        </span>
      </div>

      {/* Hidden video — source for MediaPipe only */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          opacity: 0,
          pointerEvents: "none",
        }}
      />

      {/* Canvas — dots + gaze overlay */}
      <canvas
        ref={canvasRef}
        className="flex-1 w-full"
        style={{ transform: "scaleX(-1)", display: "block" }}
      />
    </div>
  )
}

export default FacialLandmarksMirror
